import {objectFromEntries, entries} from '@benchling/js-utils';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {CONTAINER_SAMPLE, SAMPLE_TYPE_DISPLAY} from 'constants/SampleType';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import * as ComputedFieldUtil from 'schemas/fields/computed/ComputedFieldUtil';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {AnonymousSchema} from 'schemas/SchemaConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import {ORANGE} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenuWithNavigationLoading from 'ui-library/menus/SearchableMenu';
import sortedBy from 'util/sortedBy';

import {
  COLUMN_TYPE,
  COLUMN_TYPE_TO_NAME,
  COLUMN_TYPE_TO_SORT_ORDER,
  EXTRA_FILE_COLUMN_TYPES,
  FINAL_CONCENTRATION_COLUMN_TYPES,
  IMPORT_TYPE,
  INGREDIENT_COMPONENT_BIOENTITY_COLUMN_TYPES,
  INGREDIENT_COMPONENT_LOT_COLUMN_TYPES,
  INPUT_CONCENTRATION_COLUMN_TYPES,
  PARENT_STORABLE_COLUMN_TYPES,
  PARSABLE_FIELD_TYPES,
  SCHEMA_FIELD_COLUMN_TYPES,
  SelectedSchemaIds,
  SpreadsheetColumn,
  STORABLE_BARCODE_COLUMN_TYPES,
  TRANSFER_INPUT_COLUMN_TYPES,
  TRANSFER_MEASUREMENT_TYPES,
  TRANSFER_OUTPUT_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';

// TODO(BNCH-119842): Consider refactoring these MenuOption interfaces or replacing with a shared component.
export interface SubmenuOption {
  disabled?: boolean;
  key?: string;
  name: string | JSX.Element;
  pureTextName?: string;
  handleOnClick?: () => void;
}

interface MenuOption {
  handleOnClick: null | (() => void);
  key?: string;
  name: string | JSX.Element;
  pureTextName?: string;
  submenu: Array<SubmenuOption | null> | null | Array<MenuOption>;
}

interface SortableMenuOption extends MenuOption {
  sortOrder: number;
}

export interface ColumnTypeDropdownProps {
  className: string | null;
  column: SpreadsheetColumn;
  columnIdx: number;
  columnTypes: Array<COLUMN_TYPE>;
  dataTableKeyFields: Array<COLUMN_TYPE>;
  getMenuItemName: (columnType: COLUMN_TYPE) => string;
  getValidSchemaFieldIds: (columnIndex: number) => Array<string>;
  schemaFieldsToHideByColumnType?: Map<COLUMN_TYPE, Array<string>>;
  importType: IMPORT_TYPE;
  onChangeColumn: (
    columnIndex: number,
    newColumnType: COLUMN_TYPE,
    newExtraValues: {
      newSchemaField?: SchemaFieldConstants.SchemaField;
      newTableColumnName?: string;
      newSampleType?: keyof typeof SAMPLE_TYPE_DISPLAY;
      newCustomSyntaxId?: string;
      newComplexPolymerComponent?: {name: string; schemaFieldId: string};
    }
  ) => void;
  selectedSchemaIds: SelectedSchemaIds;
  tableColumnNames: Array<string>;
  additionalSubmenuOptionsByColumnType?: {[accessor: string]: Array<SubmenuOption>} | null;
}

export default class ColumnTypeDropdown extends React.Component<ColumnTypeDropdownProps> {
  // Maps field type strings to more descriptive labels. The bioentity link field type is labeled with an
  // asterisk
  FIELD_TYPE_NAMES = objectFromEntries(
    Object.values(SchemaFieldConstants.FIELD_TYPES).map((fieldType) => [fieldType.type, fieldType.label])
  );

  private getDefaultColumnTypeDisplayName(column: SpreadsheetColumn): string {
    if (column.type === COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD) {
      const displaySampleType = SAMPLE_TYPE_DISPLAY[column.sampleType!];
      return `Set ${column.schemaField!.name} ${displaySampleType}`;
    } else if (this.isSchemaField(column.type)) {
      return column.schemaField!.displayName;
    } else if (column.type === COLUMN_TYPE.DATA_TABLE_MATCHER) {
      const displayName =
        // eslint-disable-next-line benchlint/no-in-operator
        column.tableColumnName! in COLUMN_TYPE_TO_NAME
          ? COLUMN_TYPE_TO_NAME[column.tableColumnName as keyof typeof COLUMN_TYPE_TO_NAME]
          : column.tableColumnName;
      return `Match ${displayName}`;
    } else if (column.type === COLUMN_TYPE.DATA_TABLE_VALUE) {
      return `Set ${column.tableColumnName}`;
    } else if (column.type === COLUMN_TYPE.CUSTOM_MOD_SYNTAX) {
      assert(column.customSyntaxId !== undefined, 'Expected customSyntaxId to be set');
      return BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID[column.customSyntaxId] ?? column.customSyntaxId;
    } else if (column.type === COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT) {
      assert(column.complexPolymerComponent !== undefined, 'Expected complexPolymerComponent to be set');
      return column.complexPolymerComponent.name;
    }
    return this.getMenuItemName(column.type);
  }

  private getUnitSymbolTextIfEnabled(field?: SchemaFieldConstants.SchemaField): string | null {
    const unitSymbol = field?.unitSymbol;
    const isUnitAware = unitSymbol != null;
    return isUnitAware ? ` (${unitSymbol})` : null;
  }

  private getColumnTypeDisplayName(column: SpreadsheetColumn): string | JSX.Element {
    const defaultDisplayName = this.getDefaultColumnTypeDisplayName(this.props.column);
    const unitSymbol = this.getUnitSymbolTextIfEnabled(this.props.column.schemaField);
    const displayName = unitSymbol != null ? `${defaultDisplayName}${unitSymbol}` : defaultDisplayName;

    if (SpreadsheetBulkImportUtil.isUnconfirmedIgnoredColumn(column)) {
      return (
        <Tooltip text="Unable to identify this column type. Select a type or confirm 'Ignore'.">
          <span>
            <BenchlingIcon icon="exclamation-triangle" color={ORANGE} className="u-mini-spacing-right" />
            {displayName}
          </span>
        </Tooltip>
      );
    }

    return displayName;
  }

  private sampleLinkSubmenuOption(
    columnIndex: number,
    columnType: COLUMN_TYPE,
    field: SchemaFieldConstants.SchemaField,
    sampleType: keyof typeof SAMPLE_TYPE_DISPLAY
  ): SubmenuOption {
    return {
      key: `${field.name}-${sampleType}`,
      name: `Set ${field.name} ${SAMPLE_TYPE_DISPLAY[sampleType]}`,
      handleOnClick: () => {
        this.props.onChangeColumn(columnIndex, columnType, {
          newSchemaField: field,
          newSampleType: sampleType,
        });
      },
    };
  }

  private getSampleGroupSchemaFieldSubmenuOptions(
    columnIndex: number,
    columnType: COLUMN_TYPE,
    schema: AnonymousSchema | null
  ): Array<SubmenuOption> {
    if (!assertAndContinue(schema != null, 'No schema found for SAMPLE_GROUP_SCHEMA_FIELD columnType')) {
      return [];
    }

    return schema!.fields
      .map((field) => {
        if (!assertAndContinue(field.requiredLink != null, `Field with id=${field.id} has no requiredLink`)) {
          return [];
        }

        // @ts-expect-error: TODO
        const linkType = field.requiredLink!.sampleType;
        return [
          this.sampleLinkSubmenuOption(columnIndex, columnType, field, linkType),
          this.sampleLinkSubmenuOption(columnIndex, columnType, field, CONTAINER_SAMPLE),
        ];
      })
      .flat();
  }

  private getEditableFields(
    schema: AnonymousSchema,
    columnIndex: number
  ): Array<SchemaFieldConstants.SchemaField> {
    const nonComputedFields = ComputedFieldUtil.nonComputedFields(schema);
    const assayFieldTypes = new Set([
      SchemaFieldConstants.FIELD_TYPES.ASSAY_RUN_LINK.type,
      SchemaFieldConstants.FIELD_TYPES.ASSAY_REQUEST_LINK.type,
      SchemaFieldConstants.FIELD_TYPES.ASSAY_RESULT_LINK.type,
      SchemaFieldConstants.FIELD_TYPES.BLOB_LINK.type,
    ]);
    return nonComputedFields.filter(
      (field) =>
        !assayFieldTypes.has(field.fieldType) &&
        PARSABLE_FIELD_TYPES.includes(field.fieldType) &&
        (this.props.getValidSchemaFieldIds
          ? this.props.getValidSchemaFieldIds(columnIndex).includes(field.apiId)
          : true)
    );
  }

  private getDataTableMatcherSubmenuOptions(
    columnIndex: number,
    columnType: COLUMN_TYPE
  ): Array<SubmenuOption> | null {
    if (
      !assertAndContinue(
        this.props.tableColumnNames != null,
        'Expected a non-null tableColumnNames if DATA_TABLE_MATCHER is available.'
      ) ||
      !assertAndContinue(
        this.props.dataTableKeyFields != null,
        'Expected a non-null dataTableKeyFields if DATA_TABLE_MATCHER is available.'
      )
    ) {
      return null;
    }

    const keyFieldItems = this.props.dataTableKeyFields.map((name) => ({
      name: `Match ${COLUMN_TYPE_TO_NAME[name]}`,
      key: name,
      handleOnClick: () => {
        this.props.onChangeColumn(columnIndex, columnType, {newTableColumnName: name});
      },
    }));
    const tableColumnItems = this.props.tableColumnNames.map((name) => ({
      name: `Match ${name}`,
      key: name,
      handleOnClick: () => {
        this.props.onChangeColumn(columnIndex, columnType, {newTableColumnName: name});
      },
    }));
    return [...keyFieldItems, ...tableColumnItems];
  }

  private getDataTableValueSubmenuOptions(
    columnIndex: number,
    columnType: COLUMN_TYPE
  ): Array<SubmenuOption> | null {
    if (
      !assertAndContinue(
        this.props.tableColumnNames != null,
        'Expected a non-null tableColumnNames if DATA_TABLE_VALUE is available.'
      )
    ) {
      return null;
    }

    return this.props.tableColumnNames.map((name) => ({
      name: `Set ${name}`,
      key: name,
      handleOnClick: () => {
        this.props.onChangeColumn(columnIndex, columnType, {newTableColumnName: name});
      },
    }));
  }

  private getComplexPolymerComponentSubmenuOptions(
    columnIndex: number,
    columnType: COLUMN_TYPE
  ): Array<SubmenuOption> | null {
    assert(
      this.props.additionalSubmenuOptionsByColumnType != null &&
        this.props.additionalSubmenuOptionsByColumnType[COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT] != null,
      'Expected complex polymer component submenu options to exist when importing complex polymer schema entities'
    );

    const submenuOptions =
      this.props.additionalSubmenuOptionsByColumnType[COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT];
    return submenuOptions.map((option) => ({
      ...option,
      handleOnClick: () => {
        this.props.onChangeColumn(columnIndex, columnType, {
          newComplexPolymerComponent: {name: option.name as string, schemaFieldId: option.key!},
        });
      },
    }));
  }

  private getSchema(columnType: COLUMN_TYPE): AnonymousSchema | null {
    if (SCHEMA_FIELD_COLUMN_TYPES.includes(columnType)) {
      return SpreadsheetBulkImportUtil.getSchemaFromColumnType(columnType, this.props.selectedSchemaIds);
    }
    return null;
  }

  // Not private because it's used in tests
  getSubmenuOptions(columnIndex: number, columnType: COLUMN_TYPE): Array<SubmenuOption> | null {
    if (columnType === COLUMN_TYPE.CUSTOM_MOD_SYNTAX) {
      const sortedCustomSyntaxEntries = sortedBy(
        [...entries(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID)],
        [{accessor: ([, syntaxName]) => syntaxName, naturalSort: true}]
      );
      return sortedCustomSyntaxEntries.map(([syntaxId, syntaxName]) => ({
        name: syntaxName,
        handleOnClick: () => {
          this.props.onChangeColumn(columnIndex, columnType, {newCustomSyntaxId: syntaxId});
        },
      }));
    }

    const schema = this.getSchema(columnType);
    if (columnType === COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD) {
      return this.getSampleGroupSchemaFieldSubmenuOptions(columnIndex, columnType, schema);
    }

    if (schema) {
      const editableFields = this.getEditableFields(schema, columnIndex);
      if (editableFields.length === 0) {
        return [
          {
            disabled: true,
            name: 'No editable fields defined for schema',
          },
        ];
      } else {
        const schemaFieldsToHide = this.props.schemaFieldsToHideByColumnType?.get(columnType) ?? [];

        const schemaFields = editableFields
          .filter((field) => !schemaFieldsToHide.includes(field.displayName))
          .map((field) => {
            const isBioentityLink = TagSchemaUtil.isItemLinkFieldType(field.fieldType);
            const unitSymbol = this.getUnitSymbolTextIfEnabled(field);
            return {
              name: (
                <span>
                  {field.displayName}
                  <span
                    className={classNames('spreadsheetColumnsTable-field-type', {
                      'spreadsheetColumnsTable-bioentity-link-field': isBioentityLink,
                    })}
                  >
                    {isBioentityLink && '* '}
                    {this.FIELD_TYPE_NAMES[field.fieldType]}
                    {unitSymbol}
                  </span>
                </span>
              ),
              pureTextName: field.displayName,
              handleOnClick: () => {
                this.props.onChangeColumn(columnIndex, columnType, {newSchemaField: field});
              },
            };
          });
        return schemaFields;
      }
    }
    if (columnType === COLUMN_TYPE.DATA_TABLE_MATCHER) {
      return this.getDataTableMatcherSubmenuOptions(columnIndex, columnType);
    }
    if (columnType === COLUMN_TYPE.DATA_TABLE_VALUE) {
      return this.getDataTableValueSubmenuOptions(columnIndex, columnType);
    }
    if (columnType === COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT) {
      return this.getComplexPolymerComponentSubmenuOptions(columnIndex, columnType);
    }
    return null;
  }

  private isSchemaField(columnType: COLUMN_TYPE): boolean {
    return SpreadsheetBulkImportUtil.getColumnTypeToSendToServer(columnType) === COLUMN_TYPE.SCHEMA_FIELD;
  }

  private isDataTableField(columnType: COLUMN_TYPE): boolean {
    return [COLUMN_TYPE.DATA_TABLE_VALUE, COLUMN_TYPE.DATA_TABLE_MATCHER].includes(columnType);
  }

  private isNonClickableColumnType(columnType: COLUMN_TYPE): boolean {
    return (
      this.isSchemaField(columnType) ||
      this.isDataTableField(columnType) ||
      columnType === COLUMN_TYPE.CUSTOM_MOD_SYNTAX ||
      columnType === COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT
    );
  }

  private getHandleOnClick(columnIndex: number, columnType: COLUMN_TYPE): null | (() => void) {
    if (this.isNonClickableColumnType(columnType)) {
      return null;
    }
    return () => {
      this.props.onChangeColumn(columnIndex, columnType, {});
    };
  }

  private getMenuItemsForColumnTypes(
    columnTypes: Array<COLUMN_TYPE>,
    columnIndex: number
  ): Array<SortableMenuOption> {
    return columnTypes.map((columnType) => ({
      name: this.getMenuItemName(columnType),
      sortOrder: COLUMN_TYPE_TO_SORT_ORDER[columnType]!,
      submenu: this.getSubmenuOptions(columnIndex, columnType),
      handleOnClick: this.getHandleOnClick(columnIndex, columnType),
    }));
  }

  // Not private because it's used in tests :-\
  hasBothBarcodeColumns(): boolean {
    const barcodeColumns = _.intersection(this.props.columnTypes, STORABLE_BARCODE_COLUMN_TYPES);
    return barcodeColumns.length > 1;
  }

  // Not private because it's used in tests :-\
  getMenuItemName(columnType: COLUMN_TYPE): string {
    if (this.hasBothBarcodeColumns() && columnType === COLUMN_TYPE.BARCODE) {
      return 'Current barcode';
    }
    if (
      columnType === COLUMN_TYPE.FINAL_QUANTITY_VALUE &&
      !this.props.columnTypes.includes(COLUMN_TYPE.TRANSFER_QUANTITY)
    ) {
      return getQuantityDisplayString();
    }
    return this.props.getMenuItemName(columnType);
  }

  private getOptionData(menuOption: MenuOption | SubmenuOption): OptionData {
    return {
      name: menuOption.name,
      value: menuOption,
      // For browser tests to more easily find uncontained floating submenu options
      dataTestAttrs: {component: 'OptionContainer', element: 'root'},
      submenu:
        // eslint-disable-next-line benchlint/no-in-operator
        'submenu' in menuOption && menuOption.submenu != null
          ? [
              menuOption.submenu.map((subItem: SubmenuOption | MenuOption | null) =>
                subItem == null ? null : this.getOptionData(subItem)
              ),
            ]
          : null,
      ...(menuOption.pureTextName && {pureTextName: menuOption.pureTextName}),
    };
  }

  private getDropdownMenuForColumn(i: number): JSX.Element {
    // First pull out subsets of column types that will be rendered as submenus, then add in the remaining
    // column types.
    const allPossibleMeasurementColumns = [
      ...TRANSFER_MEASUREMENT_TYPES,
      ...FINAL_CONCENTRATION_COLUMN_TYPES,
    ];
    const barcodeColumns = _.intersection(this.props.columnTypes, STORABLE_BARCODE_COLUMN_TYPES);
    const barcodeColumnTypes = this.hasBothBarcodeColumns() ? barcodeColumns : [];
    const transferInputColumnTypes = _.intersection(this.props.columnTypes, TRANSFER_INPUT_COLUMN_TYPES);
    const transferOutputColumnTypes = _.intersection(this.props.columnTypes, TRANSFER_OUTPUT_COLUMN_TYPES);
    const parentStorableColumnTypes = _.intersection(this.props.columnTypes, PARENT_STORABLE_COLUMN_TYPES);
    const measurementColumnTypes = _.intersection(this.props.columnTypes, allPossibleMeasurementColumns);
    const mixtureIngredientComponentBioentityColumnTypes = _.intersection(
      this.props.columnTypes,
      INGREDIENT_COMPONENT_BIOENTITY_COLUMN_TYPES
    );
    const mixtureIngredientComponentLotColumnTypes = _.intersection(
      this.props.columnTypes,
      INGREDIENT_COMPONENT_LOT_COLUMN_TYPES
    );
    const extraColumnTypes = _.intersection(this.props.columnTypes, EXTRA_FILE_COLUMN_TYPES);
    // eslint-disable-next-line no-restricted-syntax
    const columnTypes = _.without(
      this.props.columnTypes,
      ...barcodeColumnTypes,
      ...transferInputColumnTypes,
      ...transferOutputColumnTypes,
      ...parentStorableColumnTypes,
      ...measurementColumnTypes,
      ...mixtureIngredientComponentBioentityColumnTypes,
      ...mixtureIngredientComponentLotColumnTypes,
      ...extraColumnTypes
    );
    const menuItems = this.getMenuItemsForColumnTypes(columnTypes, i);
    if (barcodeColumnTypes.length) {
      menuItems.push({
        name: 'Barcode',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[barcodeColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(barcodeColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (transferInputColumnTypes.length) {
      menuItems.push({
        name: 'Source',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[transferInputColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(transferInputColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (transferOutputColumnTypes.length) {
      menuItems.push({
        name: 'Destination',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[transferOutputColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(transferOutputColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (extraColumnTypes.length) {
      menuItems.push({
        name: 'Optional entity types',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[extraColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(extraColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (parentStorableColumnTypes.length) {
      menuItems.push({
        name: 'Stored in',
        key: 'storage',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[parentStorableColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(parentStorableColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (measurementColumnTypes.length) {
      menuItems.push({
        name: _.intersection(measurementColumnTypes, INPUT_CONCENTRATION_COLUMN_TYPES).length
          ? `${getQuantityDisplayString()}/Concentration`
          : getQuantityDisplayString(),
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[measurementColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(measurementColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (mixtureIngredientComponentBioentityColumnTypes.length) {
      menuItems.push({
        name: 'Ingredient component entity',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[mixtureIngredientComponentBioentityColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(mixtureIngredientComponentBioentityColumnTypes, i),
        handleOnClick: null,
      });
    }
    if (mixtureIngredientComponentLotColumnTypes.length) {
      menuItems.push({
        name: 'Ingredient component lot',
        sortOrder: COLUMN_TYPE_TO_SORT_ORDER[mixtureIngredientComponentLotColumnTypes[0]]!,
        submenu: this.getMenuItemsForColumnTypes(mixtureIngredientComponentLotColumnTypes, i),
        handleOnClick: null,
      });
    }
    // Sort menu items based on values defined by COLUMN_TYPE_TO_SORT_ORDER
    const sortedMenuItems = menuItems.sort((a, b) => a.sortOrder - b.sortOrder);

    return (
      <SearchableMenuWithNavigationLoading
        className="bulk-import--column-type-dropdown"
        optionDatas={sortedMenuItems.map((menuItem) => this.getOptionData(menuItem))}
        onSelect={(option) => (option.handleOnClick ? option.handleOnClick() : null)}
        scrollable={true}
      />
    );
  }

  render(): JSX.Element {
    return (
      <SelectDropdown
        displayName={this.getColumnTypeDisplayName(this.props.column)}
        menu={this.getDropdownMenuForColumn(this.props.columnIdx)}
        className={this.props.className}
        dataTestAttrs={{
          component: 'SpreadsheetColumnsTable',
          element: 'columnTypeDropdown',
        }}
      />
    );
  }
}
